import CertificateIcon from '@/assets/icons/cetrificate.svg?react';
import { Loadable } from '@/components/loaders';
import { Button } from '@/lib';
import { BaseModal } from '@/lib/Modals';
import { useAppDispatch } from '@/reducers';
import { useGetTermsQuery } from '@/services/endpoints/support/endpoints';
import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useReducer } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { z } from 'zod';
import { logout } from '../auth/authSlice';
import { TermsAndConditionsAcceptButton } from './TermsAndConditionsAcceptButton';
import { TermsAndConditionsLink } from './TermsAndConditionsLink';
import { TERMS_AND_CONDITIONS_FORM_SCHEMA } from './formSchema';

type TermsAndConditionsFormData = z.infer<typeof TERMS_AND_CONDITIONS_FORM_SCHEMA>;

export const TermsAndConditionsModal: FC = () => {
  const { t } = useTranslation();
  const [isOpen, toggleModal] = useReducer((state) => !state, true);

  const { data, isLoading } = useGetTermsQuery();

  const dispatch = useAppDispatch();

  const {
    register,
    watch,
    formState: { errors },
  } = useForm<TermsAndConditionsFormData>({
    resolver: zodResolver(TERMS_AND_CONDITIONS_FORM_SCHEMA),
    defaultValues: {
      termsAccepted: false,
      termsAcceptedAt: new Date(),
      privacyAccepted: false,
    },
    mode: 'onChange',
  });

  const watchedValues = watch();

  const onRejectClicked = () => {
    dispatch(logout());
  };

  if (!isOpen) {
    return null;
  }

  return (
    <BaseModal onClose={toggleModal} classNames={{ content: 'items-center  text-pretty', dialog: 'max-w-xl' }} isDismissable={false}>
      <Loadable isLoading={isLoading}>
        <CertificateIcon />
        <h1 className='font-semibold text-primary-900'>{t('ALL:termsAndConditions.title')}</h1>
        <h2 className='hyphens-auto break-words text-justify text-xs leading-[1.125rem] text-tertiary-600'>
          <Trans
            i18nKey='ALL:termsAndConditions.message1'
            components={{
              termsAndConditions: <TermsAndConditionsLink href={data?.documents.terms_and_conditions.url || '#'}>Terms and Conditions</TermsAndConditionsLink>,
              privacyPolicy: <TermsAndConditionsLink href={data?.documents.privacy_policy.url || '#'}>Privacy Policy</TermsAndConditionsLink>,
            }}
          />
        </h2>
        <form className='w-full space-y-4 pt-6'>
          <div className='space-y-2'>
            <div className='flex items-start gap-2'>
              <input
                {...register('termsAccepted')}
                type='checkbox'
                className='size-4 translate-y-[2.5px] rounded border-quaternary-300 text-brand-600 focus:ring-brand-500 disabled:bg-brand-100 disabled:text-brand-400'
              />
              <label className='cursor-pointer text-sm text-quaternary-700'>
                <Trans
                  i18nKey='ALL:termsAndConditions.checkboxTerms'
                  components={{
                    termsAndConditions: (
                      <TermsAndConditionsLink href={data?.documents.terms_and_conditions.url || '#'}>Terms and Conditions</TermsAndConditionsLink>
                    ),
                  }}
                />
              </label>
            </div>
            {errors.termsAccepted && <p className='ml-6 text-sm text-red-600'>{errors.termsAccepted.message}</p>}
          </div>

          <div className='space-y-2'>
            <div className='flex items-start gap-2'>
              <input
                {...register('privacyAccepted')}
                type='checkbox'
                className='size-4 translate-y-[2.5px] rounded border-quaternary-300 text-brand-600 focus:ring-brand-500 disabled:bg-brand-100 disabled:text-brand-400'
              />
              <label className='cursor-pointer text-sm text-quaternary-700'>
                <Trans
                  i18nKey='ALL:termsAndConditions.checkboxPrivacy'
                  components={{
                    privacyPolicy: <TermsAndConditionsLink href={data?.documents.privacy_policy.url || '#'}>Privacy Policy</TermsAndConditionsLink>,
                  }}
                />
              </label>
            </div>
            {errors.privacyAccepted && <p className='ml-6 text-sm text-red-600'>{errors.privacyAccepted.message}</p>}
          </div>
        </form>

        <div className='flex gap-3 pt-9'>
          <Button text={t('COMMON:reject')} onClick={onRejectClicked} intent='secondary' size='lg' className='capitalize' />
          <TermsAndConditionsAcceptButton onAccept={toggleModal} disabled={!watchedValues.termsAccepted || !watchedValues.privacyAccepted} />
        </div>
      </Loadable>
    </BaseModal>
  );
};
